import type { NewsItem, Review, SearchCategory, Work, WorkType } from "@/types";
import { works } from "@/mocks/works";
import { news } from "@/mocks/news";
import { reviews } from "@/mocks/reviews";

export { works, news, reviews };

export function getWorkById(id: string): Work | undefined {
  return works.find((w) => w.id === id);
}

export function getNewsById(id: string): NewsItem | undefined {
  return news.find((n) => n.id === id);
}

export function getReviewById(id: string): Review | undefined {
  return reviews.find((r) => r.id === id);
}

export interface WorkFilters {
  type?: WorkType;
  genre?: string;
  region?: string;
  year?: string;
  sort?: "latest" | "hot";
  airStatus?: string;
}

export function filterWorks(filters: WorkFilters): Work[] {
  const { type, genre = "全部", region = "全部", year = "全部", sort = "latest", airStatus = "全部" } = filters;
  let list = works.filter((w) => (type ? w.type === type : true));

  if (genre !== "全部") list = list.filter((w) => w.genres.includes(genre));
  if (region !== "全部") list = list.filter((w) => w.region === region);
  if (year !== "全部") list = list.filter((w) => String(w.year) === year);
  if (type === "tv" && airStatus !== "全部") list = list.filter((w) => w.airStatus === airStatus);

  if (sort === "hot") {
    list = [...list].sort((a, b) => b.rating - a.rating);
  } else {
    list = [...list].sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1));
  }
  return list;
}

export function collectGenres(type?: WorkType): string[] {
  const set = new Set<string>();
  works.filter((w) => (type ? w.type === type : true)).forEach((w) => w.genres.forEach((g) => set.add(g)));
  return ["全部", ...Array.from(set)];
}

export function collectRegions(type?: WorkType): string[] {
  const set = new Set<string>();
  works.filter((w) => (type ? w.type === type : true)).forEach((w) => set.add(w.region));
  return ["全部", ...Array.from(set)];
}

export function collectYears(type?: WorkType): string[] {
  const set = new Set<string>();
  works.filter((w) => (type ? w.type === type : true)).forEach((w) => set.add(String(w.year)));
  return ["全部", ...Array.from(set).sort((a, b) => Number(b) - Number(a))];
}

/** 相关推荐：基于类型与标签的相似度演示 */
export function getRelatedWorks(work: Work, limit = 4): Work[] {
  return works
    .filter((w) => w.id !== work.id)
    .map((w) => {
      let score = 0;
      if (w.type === work.type) score += 2;
      score += w.genres.filter((g) => work.genres.includes(g)).length * 3;
      if (w.region === work.region) score += 1;
      return { w, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((item) => item.w);
}

export function getReviewsByWork(workId: string): Review[] {
  return reviews.filter((r) => r.workId === workId);
}

export function getRelatedReviews(review: Review, limit = 3): Review[] {
  return reviews
    .filter((r) => r.id !== review.id)
    .map((r) => {
      let score = 0;
      if (r.category === review.category) score += 2;
      score += r.tags.filter((t) => review.tags.includes(t)).length * 3;
      return { r, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((item) => item.r);
}

export function getRelatedNews(item: NewsItem, limit = 3): NewsItem[] {
  return news
    .filter((n) => n.id !== item.id)
    .map((n) => {
      let score = n.tags.filter((t) => item.tags.includes(t)).length * 3;
      if (n.relatedWorkId && n.relatedWorkId === item.relatedWorkId) score += 2;
      return { n, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((i) => i.n);
}

export interface SearchResultItem {
  id: string;
  category: Exclude<SearchCategory, "all">;
  title: string;
  summary: string;
  image: string;
  meta: string;
  link: string;
}

export function searchAll(query: string, category: SearchCategory = "all"): SearchResultItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const match = (text: string) => text.toLowerCase().includes(q);

  const results: SearchResultItem[] = [];

  if (category === "all" || category === "movie" || category === "tv") {
    works
      .filter((w) => {
        if (category === "movie" && w.type !== "movie") return false;
        if (category === "tv" && w.type !== "tv") return false;
        return (
          match(w.title) ||
          match(w.synopsis) ||
          match(w.director) ||
          w.genres.some(match) ||
          w.cast.some(match) ||
          match(w.region)
        );
      })
      .forEach((w) =>
        results.push({
          id: w.id,
          category: w.type,
          title: w.title,
          summary: w.synopsis,
          image: w.poster,
          meta: `${w.year} · ${w.region} · ${w.genres.join("/")}`,
          link: `/detail/work/${w.id}`,
        }),
      );
  }

  if (category === "all" || category === "review") {
    reviews
      .filter(
        (r) =>
          match(r.title) ||
          match(r.excerpt) ||
          match(r.author) ||
          match(r.workTitle) ||
          r.tags.some(match),
      )
      .forEach((r) =>
        results.push({
          id: r.id,
          category: "review",
          title: r.title,
          summary: r.excerpt,
          image: r.cover,
          meta: `影评 · ${r.author} · ${r.date}`,
          link: `/detail/review/${r.id}`,
        }),
      );
  }

  if (category === "all" || category === "news") {
    news
      .filter(
        (n) => match(n.title) || match(n.summary) || n.tags.some(match) || match(n.source),
      )
      .forEach((n) =>
        results.push({
          id: n.id,
          category: "news",
          title: n.title,
          summary: n.summary,
          image: n.cover,
          meta: `资讯 · ${n.source} · ${n.date}`,
          link: `/detail/news/${n.id}`,
        }),
      );
  }

  return results;
}